import React, { useEffect, useState } from 'react';
import { JobDetails } from '../../../entities/JobDetails';
import { initialize, windowHapi } from '../../../config/vonq';
import { Col } from 'reactstrap';
import { TokenService } from '../../../services/applicant_tracking/analytics/jobboards/TokenService';
import {
  Campaign,
  EmploymentType,
  Product,
  ProductPriceCurrency,
  SalaryPeriod,
  Wallet,
  WindowHapi,
  WindowHapiInjector,
} from '@vonq/hapi-elements-types';
import { loadTargetGroup, loadWallet } from './utils/loaders';
import {
  JobPostingDetails,
  JobPostingDetailsForm,
  TargetGroupValues,
} from './JobPostingDetails';
import { Organization } from '../../../entities/Organization';
import { HeadingRow } from '../HeadingRow';
import { Cart } from './Cart';
import { PartnerService } from '../../../services/applicant_tracking/analytics/jobboards/PartnerService';
import { User } from '../../../entities/User';
import { formIsValid } from './utils/formIsValid';
import { JobBoardsEmptyState } from './JobBoardsEmptyState';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { Marketplace } from './Marketplace';
import { PaymentData, PaymentModal } from './PaymentModal';
import { Partner } from '../../../entities/applicant_tracking/jobboards/Partner';
import {
  JobBoardAlert,
  JobBoardAlertObject,
  JobBoardAlertType,
} from './JobBoardAlert';

interface PropTypes {
  jobDetails: JobDetails;
  organization: Organization;
  user: User;
}

export type WindowWithHapi = WindowHapi & Window & WindowHapiInjector;

export function JobBoardsTab(props: PropTypes) {
  const window = windowHapi();

  const [isLoading, setIsLoading] = useState(true);
  const [loadingFailed, setLoadingFailed] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [cartAmount, setCartAmount] = useState<number>(null);
  const [wallet, setWallet] = useState<Wallet>(null);
  const [partnerData, setPartnerData] = useState<Partner>(null);
  const [form, setForm] = useState<JobPostingDetailsForm>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState<boolean>(false);
  const [_paymentSuccessful, setPaymentSuccessful] = useState<boolean>(false);

  const [targetGroupValues, setTargetGroupValues] =
    useState<TargetGroupValues>(null);
  const [paymentData, setPaymentData] = useState<PaymentData>(null);
  const [alert, setAlert] = useState<JobBoardAlertObject>(null);
  const [errors, setErrors] = useState<any>(null);

  function mapIndustryAndCategory(data: {
    id: number | string;
    description: string;
  }) {
    return { description: data.description };
  }

  async function handleSaveClick(): Promise<Campaign> {
    const campaignForm = {
      companyId: props.organization.id.toString(),
      currency: ProductPriceCurrency.USD,
      recruiterInfo: {
        id: props.user.id,
        name: props.user.name,
        emailAddress: props.user.email,
      },
      campaignName: form.campaignName,
      postingDetails: {
        title: form.title,
        description: form.editorStateHtml,
        organization: {
          name: props.organization.name,
          companyLogo:
            'https://static.otta.com/uploads/images/company-logos/4443-4S3E4P9TxJw5JMA20R7OkbIKv9uj5-y4K_S9Fw4UwT4.png', //props.organization.organizationInfo.logoUrl,
        },
        workingLocation: {
          addressLine1: form.address1,
          addressLine2: form.address2,
          postcode: form.postcode,
          city: form.city,
          country: form.country,
          allowsRemoteWork: form.remote,
        },
        contactInfo: {
          name: form.contactName,
          phoneNumber: form.contactPhone,
          emailAddress: form.contactEmail,
        },
        yearsOfExperience: form.yearsOfExperience,
        employmentType: EmploymentType.permanent,
        weeklyWorkingHours: {
          from: form.weeklyHoursFrom,
          to: form.weeklyHoursTo,
        },
        salaryIndication: {
          period: form.salaryPeriod as SalaryPeriod,
          range: {
            from: form.salaryRangeFrom,
            to: form.salaryRangeTo,
            currency: form.salaryCurrency,
          },
        },
        jobPageUrl: form.jobPostUrl,
        applicationUrl: form.applicationFormUrl,
      },
      targetGroup: {
        educationLevel: [form.educationLevel],
        seniority: [form.seniority],
        industry: [mapIndustryAndCategory(form.industry)],
        jobCategory: [mapIndustryAndCategory(form.jobCategory)],
      },
      orderedProducts:
        // @ts-expect-error the vonq documentation is not updated with the value method.
        window.hapi.campaign.state.campaignForm.value.orderedProducts,
      labels: { jobId: props.jobDetails.id.toString() },
      paymentMethod:
        window.hapi.orderJourney.utils.paymentMethodKeys.directCharge,
      walletId: wallet.id,
    };

    // @ts-expect-error the vonq documentation is not updated with the value method.
    return await window.hapi.campaign.api.orderCampaign(campaignForm);
  }

  async function handleRemoveProduct(product: Product): Promise<void> {
    try {
      await window.hapi.basket.service.removeProductOrContractById.run(
        product.product_id,
      );
      setAlert({
        type: JobBoardAlertType.ProductRemoved,
        message: (
          <span>
            <b>{product.title}</b> was removed from the cart
          </span>
        ),
      });
    } catch (e: any) {
      setAlert({
        type: JobBoardAlertType.ProductRemovedError,
        message: (
          <span>
            Could not remove the <b>{product.title}</b> from the cart
          </span>
        ),
      });
      console.error(
        `Could not remove product ${product.product_id} from the basket`,
        e,
      );
    }
  }

  async function handlePay() {
    try {
      setPaymentModalOpen(true);
      const campaign = await handleSaveClick();

      setPaymentData({
        campaignId: campaign.campaignId,
        campaignName: form.campaignName,
        marketplaceUrl: partnerData.marketplaceUrl,
        partnerId: partnerData.partnerId,
        walletId: wallet.id,
      });
      setErrors(null);
    } catch (e: any) {
      setErrors(e?.response?.data?.postingDetails);
      setPaymentModalOpen(false);
      setAlert({
        type: JobBoardAlertType.FormError,
        message: 'Your form contains errors. Please review it.',
      });
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const token = await TokenService.get();
        if (token) {
          await initialize(token.token);
          setIsLoading(false);
          // @ts-expect-error the vonq documentation is not updated with the onChange method.
          window.hapi.basket.state.products.onChange((v: Product[]) =>
            setSelectedProducts(v),
          );

          // @ts-expect-error the vonq documentation is not updated with the onChange method.
          window.hapi.basket.state.totalInUSD.onChange((v: number) =>
            setCartAmount(v),
          );
          window.hapi.campaign.api.getCampaigns();
          setTargetGroupValues(await loadTargetGroup(window));
          setWallet(await loadWallet(window, props.organization.name));
          setPartnerData(await PartnerService.get());
        } else {
          setLoadingFailed(true);
          setIsLoading(false);
        }
      } catch (error) {
        console.error(error);
        setLoadingFailed(true);
        setIsLoading(false);
      }
    })();
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {alert != null && <JobBoardAlert message={alert} setMessage={setAlert} />}
      {paymentModalOpen && (
        <PaymentModal
          isOpen={paymentModalOpen}
          paymentData={paymentData}
          setIsOpen={setPaymentModalOpen}
          setAlert={setAlert}
          setSuccess={setPaymentSuccessful}
        />
      )}
      <HeadingRow tabName='Job Boards'>
        <Cart
          campaignName={form?.campaignName}
          cartAmount={cartAmount}
          products={selectedProducts}
          wallet={wallet}
          window={window}
          setWallet={setWallet}
          formIsValid={formIsValid(form)}
          onRemoveProduct={handleRemoveProduct}
          onPay={handlePay}
        />
      </HeadingRow>
      {loadingFailed ? (
        <JobBoardsEmptyState />
      ) : (
        <div className='d-flex gap-3 mt-4'>
          <Col xs='8'>
            <Marketplace
              selectedProducts={selectedProducts}
              targetGroupValues={targetGroupValues}
              window={window}
              setAlert={setAlert}
              setLoadingFailed={setLoadingFailed}
            />
          </Col>
          <Col xs='4'>
            <JobPostingDetails
              form={form}
              setForm={setForm}
              setAlert={setAlert}
              errors={errors}
              targetGroupValues={targetGroupValues}
              jobDetails={props.jobDetails}
              organization={props.organization}
            />
          </Col>
        </div>
      )}
    </>
  );
}
